<script setup lang="ts">
import { useMutation } from "@tanstack/vue-query";
import {
    confirmPasswordReset,
    getAuth,
    sendPasswordResetEmail,
    verifyPasswordResetCode,
} from "firebase/auth";
import { reactive, computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import BackgoundImage from "../assets/images/login-background.jpeg";
import { NSpin } from "naive-ui";
import PasswordValidator from "../components/PasswordValidator.vue";
import { notifyPasswordReset } from "../services/user";

const state = reactive({
    email: "",
    password: "",
});

const route = useRoute();
const router = useRouter();
const mode = route.query.mode;
const oobCode = route.query.oobCode;

const isLoading = computed<boolean>(() => {
    return isSending.value || isResetting.value;
});

const {
    mutate: sendResetEmail,
    isError: hasSendError,
    isPending: isSending,
    isSuccess: hasSent,
} = useMutation({
    mutationFn: async function () {
        await sendPasswordResetEmail(getAuth(), state.email);
    },
});

const resetValid = ref(false);
function onPasswordValidChange(v: boolean) {
    resetValid.value = v;
}

const resetSuccess = ref(false);
const redirectSeconds = ref(4);
let redirectTimer: number | undefined;

const {
    mutate: onResetPassword,
    isError: hasResetError,
    isPending: isResetting,
} = useMutation({
    mutationFn: async function () {
        if (mode !== "resetPassword" || typeof oobCode !== "string") {
            throw new Error("Invalid reset password link");
        }

        let actorEmail: string | undefined = undefined;
        try {
            actorEmail = await verifyPasswordResetCode(getAuth(), oobCode);
        } catch (_e) {
            // ignore; confirmPasswordReset will handle invalid codes
        }

        await confirmPasswordReset(getAuth(), oobCode, state.password);

        // Best-effort notify via backend (no await)
        if (actorEmail) {
            state.email = actorEmail;
        }
        notifyPasswordReset({
            targetEmail: state.email || actorEmail || "",
            actorEmail: actorEmail,
            userAgent: navigator.userAgent,
        });

        resetSuccess.value = true;

        // Start countdown and redirect to login with email prefilled
        redirectSeconds.value = 10;
        redirectTimer = window.setInterval(() => {
            redirectSeconds.value -= 1;
            if (redirectSeconds.value <= 0) {
                if (redirectTimer) window.clearInterval(redirectTimer);
                router.push({ path: "/login", query: state.email ? { email: state.email } : {} });
            }
        }, 1000);
    },
});
</script>

<template>
    <div
        class="h-full bg-cover bg-center rounded-xl flex flex-center gap-4"
        :style="{ backgroundImage: `url(${BackgoundImage})` }"
    >
        <div class="w-[570px] p-6 bg-cream-dark flex flex-col gap-8 rounded-lg">
            <template v-if="isLoading">
                <n-spin class="my-20" size="large" />
            </template>
            <template v-else>
                <template v-if="mode === 'resetPassword'">
                    <template v-if="resetSuccess">
                        <div class="flex flex-col items-center gap-6 py-8">
                            <!-- Header with check icon and title -->
                            <div class="flex items-center justify-center mb-6">
                                <div
                                    class="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-3"
                                >
                                    <i class="material-symbols-outlined text-green text-4xl"
                                        >check_circle</i
                                    >
                                </div>
                                <h2 class="text-2xl text-gray-900 mr-3">Password Reset</h2>
                            </div>

                            <!-- SIGN IN NOW button -->
                            <button
                                @click="
                                    router.push({
                                        path: '/login',
                                        query: state.email ? { email: state.email } : {},
                                    })
                                "
                                class="button hover:bg-dark-900 text-white px-8 py-3 text-sm -mt-4"
                            >
                                SIGN IN NOW
                            </button>

                            <!-- Redirect countdown -->
                            <div class="text-xs text-gray-600">
                                Redirecting to login in {{ redirectSeconds }}s...
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <h1 class="h1">Reset Password</h1>
                        <p class="font-regular">Enter a new password for your account.</p>
                        <form @submit.prevent="onResetPassword()" class="flex flex-col gap-4">
                            <password-validator
                                v-model="state.password"
                                @valid-change="onPasswordValidChange"
                            />
                            <span v-if="hasResetError" class="error"
                                >Could not reset password.</span
                            >
                            <button
                                :disabled="isResetting || !resetValid"
                                type="submit"
                                class="button w-min whitespace-nowrap"
                                :class="{
                                    'transition duration-350': !isResetting,
                                    'opacity-50': isResetting || !resetValid,
                                }"
                            >
                                SET PASSWORD
                            </button>
                        </form>
                    </template>
                </template>
                <template v-else>
                    <h1 v-if="!hasSent" class="h1">Password Reset</h1>
                    <p v-if="!hasSent" class="font-regular -mt-3">
                        Enter the email associated with your Properverse Account. We'll send you a
                        secure link to reset your password.
                    </p>
                    <form
                        v-if="!hasSent"
                        @submit.prevent="sendResetEmail()"
                        class="flex flex-col gap-4"
                    >
                        <input
                            type="email"
                            v-model="state.email"
                            class="input"
                            placeholder="EMAIL"
                        />
                        <span v-if="hasSendError" class="error">Failed to send email.</span>

                        <button
                            :disabled="isSending"
                            type="submit"
                            class="button w-min text-xs"
                            :class="{
                                'transition duration-350': !isSending,
                                'opacity-50': isSending,
                            }"
                        >
                            EMAIL ME A RESET LINK
                        </button>
                    </form>

                    <!-- Success content -->
                    <div v-if="hasSent" class="text-center py-8">
                        <!-- Header with check icon and title -->
                        <div class="flex items-center justify-center mb-6">
                            <div
                                class="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-1"
                            >
                                <i class="material-symbols-outlined text-green text-3xl"
                                    >check_circle</i
                                >
                            </div>
                            <h2 class="text-2xl font-semibold text-gray-900 mr-3">Email Sent</h2>
                        </div>

                        <!-- Main message -->
                        <div class="mb-6">
                            <p class="text-gray-700 mb-2">
                                Check your inbox and click the link we sent to reset your password.
                            </p>
                            <p class="text-gray-700">
                                If you don't see it, check your spam or promotions folder.
                            </p>
                        </div>

                        <!-- Email confirmation -->
                        <div class="mb-6">
                            <p class="text-sm text-gray-600">
                                (We sent the link to {{ state.email }})
                            </p>
                        </div>

                        <!-- Resend link -->
                        <div>
                            <button
                                @click="sendResetEmail()"
                                class="text-gray-500 hover:text-gray-700 underline cursor-pointer"
                                :disabled="isSending"
                            >
                                Resend
                            </button>
                        </div>
                    </div>
                </template>
            </template>
        </div>
    </div>
</template>
